import { inject, Injectable, OnDestroy } from '@angular/core';
import { Client, ClientOption } from '@app/core/services/api/API.service';
import { ClientOptionService } from '@app/core/api/services';
import { BehaviorSubject } from 'rxjs';
import { sortJSON, UtilService } from '../../util.service';
import { take } from 'rxjs/operators';
import * as subscriptions from '@app/core/graphql/subscriptions';
import { AmplifyApiService } from '@app/core/api/services';
import {
    BrandfactoryServerlessService,
    ClientApi
} from '../../serverless/brandfactory-serverless.service';

@Injectable({
    providedIn: 'root'
})
export class ClientsService implements OnDestroy {
    private _brandfactoryServerlessService = inject(
        BrandfactoryServerlessService
    );
    private _clients$ = new BehaviorSubject<Client[]>([]);
    private _loading$ = new BehaviorSubject<boolean>(true);
    private clients: ClientApi[] = [];
    private _clientOptionsById = new Map<string, ClientOption>();
    private _isLoadingClients = false;
    private subscriptions: any[] = [];
    private _amplifyApiService = inject(AmplifyApiService);
    private _clientOptionService = inject(ClientOptionService);

    constructor(private _utilService: UtilService) {
        this.subscriptions.push(
            this._amplifyApiService
                .subscribe({ query: subscriptions.onCreateClient })
                .subscribe(observer => {
                    const data = observer.data.onCreateClient as Client;
                    this.clients.push(data);
                    this.clients = this._utilService.sortJSON(
                        this.clients,
                        'comertial_name',
                        'asc'
                    );
                    this._clients$.next(this.clients);
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onUpdateClient })
                .subscribe(observer => {
                    const data = observer.data.onUpdateClient as Client;
                    this.clients = this.clients.map((client: Client) => {
                        return client.id === data.id ? data : client;
                    });
                    this.clients = this._utilService.sortJSON(
                        this.clients,
                        'comertial_name',
                        'asc'
                    );
                    this._clients$.next(this.clients);
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onDeleteClient })
                .subscribe(observer => {
                    const data = observer.data.onDeleteClient;
                    this.clients = this.clients.filter((client: Client) => {
                        return client.id !== data.id;
                    });
                    this._clients$.next(this.clients);
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    get clients$() {
        return this._clients$.asObservable();
    }

    get loading$() {
        return this._loading$.asObservable();
    }

    getClientsSnapshot(): ClientApi[] {
        return this._clients$.getValue() ?? [];
    }

    async listClients(forceRefresh: boolean = false) {
        if (this._isLoadingClients && !forceRefresh) {
            return;
        }

        const existingClients = this._clients$.getValue();
        if (existingClients?.length > 0 && !forceRefresh) {
            return;
        }

        if (forceRefresh) {
            this._isLoadingClients = false;
        }

        this._isLoadingClients = true;
        try {
            this._loading$.next(true);
            this.clients = [];
            const [clients, clientOptions] = await Promise.all([
                this._brandfactoryServerlessService.listClients(),
                this._clientOptionService.listClientOptions()
            ]);
            const clientOptionMap = new Map<string, ClientOption>(
                (clientOptions || []).map((clientOption: ClientOption) => [
                    clientOption.id,
                    clientOption
                ])
            );
            this._clientOptionsById = clientOptionMap;

            this.clients = clients.map((client: ClientApi) => {
                const clientOption = clientOptionMap.get(client.id);
                if (!clientOption) {
                    return client;
                }

                return {
                    ...client,
                    isPremium: clientOption.isPremium,
                    isPop: clientOption.isPop,
                    isPrinted: clientOption.isPrinted,
                    allPremiumRfqs: clientOption.allPremiumRfqs,
                    allPopRfqs: clientOption.allPopRfqs,
                    allPrintedRfqs: clientOption.allPrintedRfqs,
                    showVat: clientOption.showVat,
                    tcdPayment: clientOption.tcdPayment
                };
            });
        } catch (err) {
            console.log(err);
        } finally {
            this._clients$.next(
                sortJSON(this.clients, 'comertial_name', 'asc')
            );
            this._loading$.next(false);
            this._isLoadingClients = false;
        }
    }

    getCachedClientOption(id: string): ClientOption | null {
        return this._clientOptionsById.get(id) ?? null;
    }

    async findClientById(id: string): Promise<ClientApi | undefined> {
        try {
            const clients =
                this.getClientsSnapshot().length > 0
                    ? this.getClientsSnapshot()
                    : await this.clients$.pipe(take(1)).toPromise();
            const client = clients.find(client => client.id === id);
            return client;
        } catch (error) {
            console.error('Error al buscar el cliente:', error);
            return undefined;
        }
    }
}
